import React, { useState } from "react";
import { getToken, URL } from "../../utils";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Dropzone from "../../components/Dropzone";
import CountryField from "../../components/CountryField";
import { useCountries } from "../../hooks/useCountries";
import { getCountryCode } from "../../utils/countryUtils";

export default function ProfessionalsAddForm() {
  // Définir les états pour chaque champ de formulaire
  const [nom, setNom] = useState("");
  const [secteur, setSecteur] = useState("");
  const [adresse, setAdresse] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [error, setError] = useState(null);
  const [registre, setRegistre] = useState("");
  const [carteCFE, setCarteCFE] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const token = getToken();
  const { countries } = useCountries();

  // Fonction pour traduire les noms de champs en libellés lisibles
  const getFieldDisplayName = (fieldName) => {
    const fieldLabels = {
      nom_entreprise: "Nom de l'entreprise",
      secteur_activite: "Secteur d'activité",
      registre_commerce: "Numéro de registre de commerce",
      adresse: "Adresse",
      contact: "Contact",
      email: "Email",
      pays: "Pays",
      carte_cfe: "Carte CFE",
    };
    return fieldLabels[fieldName] || fieldName;
  };

  // Gestionnaire pour l'upload de l'image de la carte CFE
  const handleCarteCFEUpload = (file) => {
    setCarteCFE(file);
  };

  // Gestionnaire de soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    const formData = new FormData();
    formData.append("nom_entreprise", nom);
    formData.append("secteur_activite", secteur);
    formData.append("registre_commerce", registre);
    formData.append("adresse", adresse);
    formData.append("contact", contact);
    formData.append("email", email);
    formData.append("pays", getCountryCode(country, countries)); // code ISO du pays

    // Ajout de l'image de la carte CFE si elle existe
    if (carteCFE) {
      formData.append("carte_cfe", carteCFE);
    }

    try {
      await axios.post(URL + "Clients/create-client-professionnel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Formulaire soumis avec succès");
      navigate("/clients/professionnels");
    } catch (error) {
      if (error.response) {
        console.warn(error);
        if (error.response.data) {
          setValidationErrors(error.response.data);
          setError("Erreurs de validation:");
          return;
        }
        setError("Erreur lors de l'envoi du formulaire");
      } else {
        setError(error.message);
      }
    }
  };

  if (error) {
    toast.error(error);
  }

  return (
    <div className="p-4 w-[min(600px,90%)] mx-auto mt-4 bg-white shadow-md rounded-md">
      <ToastContainer />
      <div className="flex gap-4 p-4 pb-0">
        <div className="p-3 bg-amber-200 rounded-full text-amber-600 grid justify-center items-center">
          <svg
            className="w-8 h-8"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
        </div>
        <div className="">
          <h1 className="text-lg font-semibold">Ajouter un professionnel</h1>
          <p className="text-sm text-gray-500">
            Ajoutez un professionnel à la liste des clients
          </p>
        </div>
      </div>
      <form className="p-4" onSubmit={handleSubmit}>
        {error && <div className="mb-4 text-red-500">{error}</div>}

        {/* Affichage amélioré des erreurs de validation */}
        {validationErrors && Object.keys(validationErrors).length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="text-red-800 font-medium mb-2">
                  Erreurs de validation détectées :
                </h3>
                <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                  {Object.entries(validationErrors).map(([field, errors]) => {
                    const errorArray = Array.isArray(errors)
                      ? errors
                      : [errors];
                    return errorArray.map((error, index) => (
                      <li key={`${field}-${index}`}>
                        <strong>{getFieldDisplayName(field)} :</strong> {error}
                      </li>
                    ));
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="mb-4">
          <label
            htmlFor="nom"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Nom de l'entreprise
          </label>
          <input
            type="text"
            id="nom"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Nom de l'entreprise"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            required
          />
          {validationErrors.nom_entreprise && (
            <p className="text-red-500 text-sm mt-1">
              {Array.isArray(validationErrors.nom_entreprise)
                ? validationErrors.nom_entreprise.join(", ")
                : validationErrors.nom_entreprise}
            </p>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="registre"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Numéro de registre de commerce
          </label>
          <input
            type="text"
            id="registre"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Numéro de registre de commerce"
            value={registre}
            onChange={(e) => setRegistre(e.target.value)}
            required
          />
          {validationErrors.registre_commerce && (
            <p className="text-red-500 text-sm mt-1">
              {Array.isArray(validationErrors.registre_commerce)
                ? validationErrors.registre_commerce.join(", ")
                : validationErrors.registre_commerce}
            </p>
          )}
        </div>

        {/* Country */}
        <div className="mb-4">
          <CountryField
            label="Pays"
            value={country}
            onChange={(selectedOption) =>
              setCountry(selectedOption ? selectedOption.value : "")
            }
            countries={countries}
            required
          />
          {validationErrors.pays && (
            <p className="text-red-500 text-sm mt-1">
              {Array.isArray(validationErrors.pays)
                ? validationErrors.pays.join(", ")
                : validationErrors.pays}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="secteur"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Secteur d'activité
          </label>
          <select
            id="secteur"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={secteur}
            onChange={(e) => setSecteur(e.target.value)}
            required
          >
            <option value="">---Choisir un secteur---</option>
            <option value="shop">Boutique</option>
            <option value="bar">Bar</option>
            <option value="restaurant">Restaurant</option>
            <option value="hôtel">Hôtel</option>
            <option value="Company">Compagnie</option>
            <option value="night_etablissement">Établissement de nuit</option>
          </select>
          {validationErrors.secteur_activite && (
            <p className="text-red-500 text-sm mt-1">
              {Array.isArray(validationErrors.secteur_activite)
                ? validationErrors.secteur_activite.join(", ")
                : validationErrors.secteur_activite}
            </p>
          )}
        </div>
        <div className="mb-4 flex gap-4">
          <div className="flex-grow">
            <label
              htmlFor="adresse"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Adresse
            </label>
            <input
              type="text"
              id="adresse"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Adresse"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
              required
            />
            {validationErrors.adresse && (
              <p className="text-red-500 text-sm mt-1">
                {Array.isArray(validationErrors.adresse)
                  ? validationErrors.adresse.join(", ")
                  : validationErrors.adresse}
              </p>
            )}
          </div>
          <div className="flex-grow">
            <label
              htmlFor="contact"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Contact
            </label>
            <input
              type="tel"
              id="contact"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
            />
            {validationErrors.contact && (
              <p className="text-red-500 text-sm mt-1">
                {Array.isArray(validationErrors.contact)
                  ? validationErrors.contact.join(", ")
                  : validationErrors.contact}
              </p>
            )}
          </div>
        </div>
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {validationErrors.email && (
            <p className="text-red-500 text-sm mt-1">
              {Array.isArray(validationErrors.email)
                ? validationErrors.email.join(", ")
                : validationErrors.email}
            </p>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="carteCFE"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Carte CFE
          </label>
          <Dropzone onFileUpload={handleCarteCFEUpload} />
          {validationErrors.carte_cfe && (
            <p className="text-red-500 text-xs">{validationErrors.carte_cfe}</p>
          )}
        </div>
        <div className="grid grid-cols-2 items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:text-white border border-gray-800 bg-gray-200 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="text-white bg-amber-600 hover:bg-amber-700 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
          >
            Ajouter
          </button>
        </div>
      </form>
    </div>
  );
}
